import { useParams } from 'react-router'
import { use, useCallback, useEffect, useMemo, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { InputProps } from '@/components/ui'
import { usePageContext } from '@/lib/context/page'
import {
  InputCellPopoverButton,
  InputTypeContainer,
  RowWithInputTypeContext,
  ShowInverseValue,
  useOrderTypeManager
} from '@/components/shared/tables'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore.ts'
import { useFormatLocalizedNumber } from '@/lib/hooks/useLocaleFormatter'
import { useTableErrorContext } from '@/feature/financial-model/ecommerce/revenues/context/TableErrorContext.tsx'
import { cn, parseInputValue } from '@/lib/utils'
import { isNullOrUndefinedOrEmpty } from '@/lib/utils/parsers.ts'
import { usePostDirectLabourData, useSwitchDirectLabourRowTypeMutation } from '@/feature/financial-model/ecommerce/cogs/api'
import {
  DirectLabourAllowedType,
  DirectLabourColumnSwitchRequest,
  DirectLabourTableRows,
  GetDirectLabourDataResponse,
  PostDirectLabourBody
} from '@/feature/financial-model/ecommerce/cogs/api/types.ts'
import { PeriodTableInput } from '@/components/shared/tables/table-cell'

type AllowedType = DirectLabourAllowedType
type DirectLabourPeriod = GetDirectLabourDataResponse['periods'][number]
type InputCellProps = {
  period: DirectLabourPeriod
  selectedKey: keyof DirectLabourTableRows
}

const useInputCell = ({ period, selectedKey }: InputCellProps) => {
  const { id: financialModelId } = useParams<{
    id: string
  }>()
  const { state } = usePageContext()
  const { rowType } = use(RowWithInputTypeContext) ?? {
    rowType: `absolute`
  }
  const [customType, setCustomType] = useOrderTypeManager<AllowedType>({
    row: period as Exclude<DirectLabourPeriod, 'config'> & {
      config: Record<string, { type: AllowedType }>
    },
    selectedKey,
    rowType: rowType as AllowedType
  })

  const financialModelSettings = useFinancialModelSettingsStore((settings: { settings: Record<string, any> }) => settings.settings[financialModelId!])
  const currency = useMemo(() => {
    if (financialModelSettings?.currency?.functionalCurrency) return financialModelSettings?.currency?.functionalCurrency
    return `EUR`
  }, [financialModelSettings?.currency?.functionalCurrency])
  const formatNumber = useFormatLocalizedNumber()

  const formatValue = useCallback(
    (val: string | number | null) => {
      if (val === null || val === ``) return ``
      let periodType = customType
      if (state.queryPeriod === `all` && period.period_type !== state.period) {
        periodType = period.config[selectedKey]?.type ?? customType
      }
      if (selectedKey === `percentage_of_direct_labour`) {
        return `${formatNumber(val, { style: `percent` })}`
      }
      const maximumFractionDigits = selectedKey === `total_fte` ? 1 : 0
      const minimumFractionDigits = selectedKey === `total_fte` ? 1 : 0
      if (periodType === `absolute`) {
        return `${formatNumber(val, {
          style: selectedKey === `total_fte` ? `decimal` : `currency`,
          currency,
          maximumFractionDigits,
          minimumFractionDigits
        })}`
      }
      return `${formatNumber(val, { style: `percent` })}`
    },
    [state.queryPeriod, state.period, period.period_type, period.config, selectedKey, formatNumber, customType, currency]
  )

  const [value, setValue] = useState<string | null>(period[selectedKey]?.toString() ?? null)
  const { addError, removeError, errors } = useTableErrorContext()
  const [hasError, setHasError] = useState(false)
  const queries = useIsFetching({
    queryKey: [`personnel-data`]
  })

  const { mutate: switchPersonnelRowType, isPending: isSwitchingRowType } = useSwitchDirectLabourRowTypeMutation(financialModelId!)

  const {
    mutate: updateDirectLabourData,
    error: updateError,
    reset: resetUpdateError,
    isPending: isUpdating,
    isSuccess: updateSuccess
  } = usePostDirectLabourData(financialModelId!)

  const handleUpdate = useCallback(() => {
    const objectToUpdate = {
      fiscal_month: period.fiscal_month,
      fiscal_year: period.fiscal_year,
      period_type: period.period_type,
      view: state.view,
      config: {
        ...period.config,
        [selectedKey]: {
          ...period.config[selectedKey],
          type: customType
        }
      },
      total_fte: period.total_fte,
      avg_annual_gross_salary: period.avg_annual_gross_salary,
      social_contributions: period.social_contributions,
      other_charges: period.other_charges,
      bonus: period.bonus,
      percentage_of_direct_labour: period.percentage_of_direct_labour,
      [selectedKey]: Number(value)
    } satisfies PostDirectLabourBody

    updateDirectLabourData(objectToUpdate)
  }, [
    period.fiscal_month,
    period.fiscal_year,
    period.period_type,
    period.config,
    period.total_fte,
    period.avg_annual_gross_salary,
    period.social_contributions,
    period.other_charges,
    period.bonus,
    period.percentage_of_direct_labour,
    state.view,
    selectedKey,
    customType,
    value,
    updateDirectLabourData
  ])

  const handleInputCellTypeChange = useCallback(
    (type: DirectLabourAllowedType) => {
      if (type === customType) return
      setCustomType(type)
      const objectToUpdate = {
        month: period.fiscal_month,
        year: period.fiscal_year,
        period_type: period.period_type,
        col_type: type,
        column_name: selectedKey,
        financial_model_id: financialModelId!
      } satisfies DirectLabourColumnSwitchRequest
      switchPersonnelRowType(objectToUpdate)
    },
    [customType, setCustomType, period.fiscal_month, period.fiscal_year, period.period_type, selectedKey, financialModelId, switchPersonnelRowType]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parseInputValue(e.target.value)
      setValue(parsedValue)
      removeError(period.id)
      resetUpdateError()
    },
    [period.id, removeError, resetUpdateError]
  )

  useEffect(() => {
    if (updateError) {
      if (updateError instanceof AxiosError) {
        const errorMessage = `max return value allowed ${updateError.response?.data.detail.max_retention_rate}: ${updateError.response?.data.detail.message}`
        addError(errorMessage, period.id)
      } else {
        addError(updateError.message, period.id)
      }
    }
  }, [addError, period.id, updateError])

  useEffect(() => {
    setHasError(errors.some(error => error.id === period.id))
  }, [errors, period.id])

  useEffect(() => {
    if (queries === 0) {
      if (!isNullOrUndefinedOrEmpty(period[selectedKey])) {
        setValue(formatValue(period[selectedKey]))
      }
    }
  }, [period, selectedKey, formatValue, queries])

  return {
    value,
    setValue,
    handleChange,
    handleUpdate,
    hasError,
    isUpdating,
    customType,
    handleInputCellTypeChange,
    rowType,
    updateSuccess,
    formatValue,
    isFetching: queries > 0 || isSwitchingRowType
  }
}

export const CogsDirectLabourInputCell = ({ period, selectedKey, ...props }: InputCellProps & InputProps) => {
  const { state } = usePageContext()

  const {
    value,
    handleChange,
    handleUpdate,
    hasError,
    isUpdating,
    setValue,
    formatValue,
    isFetching,
    customType,
    handleInputCellTypeChange,
    rowType
  } = useInputCell({ period, selectedKey })

  const { id: financialModelId } = useParams()

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (typeof e.target.value === `string`) {
      // const valueToUse = parseInputValue(e.target.value)
      setValue(period[selectedKey]?.toString() ?? ``)
    }
  }

  const handleBlur = () => {
    if (!value) {
      if (!period[selectedKey]) {
        return
      }
    }
    if (formatValue(value) === formatValue(period[selectedKey])) {
      return setValue(formatValue(period[selectedKey]))
    }
    handleUpdate()
    setValue(formatValue(value))
  }

  const formatNumber = useFormatLocalizedNumber()

  const financialModelSettings = useFinancialModelSettingsStore((settings: { settings: Record<string, any> }) => settings.settings[financialModelId!])
  const currency = useMemo(() => {
    if (financialModelSettings?.currency?.functionalCurrency) return financialModelSettings?.currency?.functionalCurrency
    return `EUR`
  }, [financialModelSettings?.currency?.functionalCurrency])

  const formatInverseValue = useCallback(
    (val: number | null) => {
      if (val === null) return ``
      if (selectedKey === `percentage_of_direct_labour`) {
        return `${formatNumber(val, { style: `percent` })}`
      }
      if (selectedKey === `total_fte`) {
        return `${formatNumber(val, { style: `decimal` })}`
      }
      return `${formatNumber(val, {
        style: `currency`,
        currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      })}`
    },
    [currency, formatNumber, selectedKey]
  )

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === `Enter`) {
      e.currentTarget.blur()
    }
  }, [])

  if (state.view === `yearly` || period.period_type !== state.period) {
    return <span>{value || `--`}</span>
  }

  return (
    <InputTypeContainer
      className={cn({
        'pr-2': rowType !== `absolute` && customType !== `absolute`
      })}
    >
      {rowType === `mix` && (
        <InputCellPopoverButton
          type={customType}
          handleSetType={handleInputCellTypeChange}
          allowedTypes={period.config[selectedKey]?.allowed_types ?? [`absolute`]}
          disabled={period.avg_annual_gross_salary === null || period.avg_annual_gross_salary === undefined}
        />
      )}
      <PeriodTableInput
        className={cn(hasError ? `text-red-400` : ``)}
        value={value ?? ``}
        disabled={isFetching || isUpdating || props.disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder='insert value'
      />
      <ShowInverseValue when={period[selectedKey] !== null && customType !== `absolute`}>
        <span className='flex-shrink-0 text-xs text-blue-400'>({formatInverseValue(period.inverse_values[selectedKey])})</span>
      </ShowInverseValue>
    </InputTypeContainer>
  )
}
